import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Crown, Share2, Trash2, Sparkles, Trophy } from 'lucide-react';
import { RedeemDrinkButton } from '@/components/RedeemDrinkButton';
import { DesignatePlacementsModal } from '@/components/DesignatePlacementsModal';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PersonalEventHostControls({ tournament, memberId, onUpdate, onDelete }) {
  const [updating, setUpdating] = useState(false);
  const [designateOpen, setDesignateOpen] = useState(false);

  const copyInvite = () => {
    if (!tournament.invite_code) {
      toast.error('No invite code yet — refresh the page');
      return;
    }
    const url = `${window.location.origin}/join/${tournament.invite_code}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied — share it with your players');
  };

  const handleStatus = async (newStatus) => {
    if (newStatus === tournament.status) return;
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/personal-events/${tournament.id}/status`, {
        host_id: memberId,
        status: newStatus,
      });
      if (newStatus === 'completed') {
        toast.success('Event completed — Silver Trophy awarded to the winner');
      } else {
        toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      }
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this personal event and all its scores? This cannot be undone.')) return;
    setUpdating(true);
    try {
      await axios.delete(`${API_URL}/personal-events/${tournament.id}?host_id=${memberId}`);
      toast.success('Personal event deleted');
      onDelete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
      setUpdating(false);
    }
  };

  return (
    <Card className="bg-secondary/5 border-secondary/30 mb-6">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-4 h-4 text-secondary" />
          <span className="text-secondary text-xs uppercase tracking-wider font-medium">Host controls</span>
          <span className="px-2 py-0.5 text-[10px] rounded-full border border-secondary/30 bg-secondary/15 text-secondary ml-2 inline-flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" /> Personal Event
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={copyInvite}
            className="bg-secondary text-black hover:bg-secondary/90 h-9"
            data-testid="host-share-invite-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share invite link
          </Button>
          <RedeemDrinkButton
            className="border-amber-500/50 hover:bg-amber-500/20 text-amber-300 h-9"
          />
          {tournament.status === 'completed' && (
            <Button
              onClick={() => setDesignateOpen(true)}
              variant="outline"
              className="border-yellow-500/50 hover:bg-yellow-500/20 text-yellow-300 h-9"
              data-testid="host-designate-placements-btn"
            >
              <Trophy className="w-4 h-4 mr-1" />
              Designate Winners
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Status:</span>
            <Select
              value={tournament.status}
              onValueChange={handleStatus}
              disabled={updating}
            >
              <SelectTrigger className="bg-muted border-white/10 text-white h-9 w-44" data-testid="host-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleDelete}
            variant="ghost"
            disabled={updating}
            className="text-muted-foreground hover:text-destructive ml-auto h-9"
            data-testid="host-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </div>
        {tournament.status === 'completed' && (
          <p className="text-xs text-muted-foreground mt-3">
            🥈 Silver Trophy awarded to the winner. All players notified by email.
          </p>
        )}
      </CardContent>

      <DesignatePlacementsModal
        open={designateOpen}
        onOpenChange={setDesignateOpen}
        tournament={tournament}
        adminId={memberId}
        onSuccess={onUpdate}
      />
    </Card>
  );
}

export default PersonalEventHostControls;
